import './sentry';
import './index.scss';
import '@cloudscape-design/global-styles/index.css';

import { I18nProvider } from '@cloudscape-design/components/i18n';
import enMessages from '@cloudscape-design/components/i18n/messages/all.en.json';
import { ProcessNames } from '@parallel-fluidics/shared/constants';
import * as Sentry from '@sentry/react';
import BackToTopProvider from 'providers/backToTopProvider';
import React from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  RouterProvider,
} from 'react-router-dom';

import App from './App';
import ThemeProvider from './features/theme/themeProvider';
import CreateEstimatePage from './pages/CreateEstimate';
import EditCADPage from './pages/EditCAD';
import EditFeedbackPage from './pages/EditFeedback';
import EditPricingPage from './pages/EditPricing';
import ErrorPage from './pages/ErrorPage';
import HMI from './pages/HMI';
import HomePage from './pages/Home';
import InventoryPage from './pages/Inventory';
import InventoryDetailPage from './pages/Inventory/detail';
import ReconciliationPage from './pages/Inventory/Reconciliation';
import LotsPage from './pages/Lots';
import AssembleLotPage from './pages/Lots/assemble';
import LotDetailPage from './pages/Lots/detail';
import TraceLotPage from './pages/Lots/trace';
import OrdersPage from './pages/Orders';
import ActiveOrdersPage from './pages/Orders/active';
import CreateOrderPage from './pages/Orders/create';
import CreatePackagePage from './pages/Orders/createPackage';
import ShippedOrdersPage from './pages/Orders/shipped';
import UpdateOrderPage from './pages/Orders/update';
import OrganizationsPage from './pages/Organizations';
import OrgDetailPage from './pages/Organizations/orgDetail';
import PartPage, { PartsListPage } from './pages/Part';
import PressHMIPage from './pages/PressHMI';
import PrintProductLabelPage from './pages/PrintProductLabel';
import PrintTravelerLabelsPage from './pages/PrintTravelerLabels';
import SummaryPage, { ProcessDataPage } from './pages/ProcessData';
import ProductsPage from './pages/Products';
import ProductDetailPage from './pages/Products/detail';
import ProjectPartsPage from './pages/ProjectParts';
import PPartDetailPage from './pages/ProjectParts/PPartDetail';
import ProjectsPage from './pages/Projects';
import QboQuotesPage from './pages/QboQuotes';
import ShipmentsPage from './pages/Shipments';
import ShipmentsCreatePage from './pages/Shipments/createShipping';
import TestStandPage from './pages/TestStand';
import ActiveTrackersPage from './pages/Trackers/active';
import ShippedTrackersPage from './pages/Trackers/shipped';
import TravelerPage from './pages/Travelers';
import UserPage from './pages/User';
import InitializeFirebaseApp from './providers/initializeFirebaseApp';
import * as serviceWorker from './serviceWorker';

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(
  createBrowserRouter,
);

const router = sentryCreateBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: 'user/:userID',
        element: <UserPage />,
      },
      {
        path: 'parts',
        element: <PartsListPage />,
      },
      {
        path: 'part/:partId',
        element: <PartPage />,
      },
      {
        path: 'part/:partId/cad',
        element: <EditCADPage />,
      },
      {
        path: 'part/:partId/feedback',
        element: <EditFeedbackPage />,
      },
      {
        path: 'part/:partId/pricing',
        element: <EditPricingPage />,
      },
      {
        path: 'orders',
        exact: true,
        element: <OrdersPage />,
      },
      {
        path: 'orders/active',
        exact: true,
        element: <ActiveOrdersPage />,
      },
      {
        path: 'orders/shipped',
        exact: true,
        element: <ShippedOrdersPage />,
      },
      {
        path: 'trackers/active',
        exact: true,
        element: <ActiveTrackersPage />,
      },
      {
        path: 'trackers/shipped',
        exact: true,
        element: <ShippedTrackersPage />,
      },
      {
        path: 'orders/create',
        exact: true,
        element: <CreateOrderPage />,
      },
      {
        path: 'orders/update/:orderId',
        exact: true,
        element: <UpdateOrderPage />,
      },
      {
        path: 'package/create/:orderId',
        exact: true,
        element: <CreatePackagePage />,
      },
      {
        path: 'projects',
        exact: true,
        element: <ProjectsPage />,
      },
      {
        path: 'products',
        exact: true,
        element: <ProductsPage />,
      },
      {
        path: 'products/:productDocId',
        exact: true,
        element: <ProductDetailPage />,
      },
      {
        path: 'inventory',
        exact: true,
        element: <InventoryPage />,
      },
      {
        path: 'inventory/:productDocId/:versionId',
        exact: true,
        element: <InventoryDetailPage />,
      },
      {
        path: 'inventory/:productDocId/:versionId/reconcile',
        exact: true,
        element: <ReconciliationPage />,
      },
      {
        path: 'lots',
        exact: true,
        element: <LotsPage />,
      },
      {
        path: 'lots/assemble',
        exact: true,
        element: <AssembleLotPage />,
      },
      {
        path: 'lots/trace',
        exact: true,
        element: <TraceLotPage />,
      },
      {
        path: 'lots/:lotId',
        exact: true,
        element: <LotDetailPage />,
      },
      {
        path: 'projects/:projectDocId',
        exact: true,
        element: <ProjectPartsPage />,
      },
      {
        path: 'projects/:projectDocId/part/:partDocId',
        exact: true,
        element: <PPartDetailPage />,
      },
      {
        path: 'qbo/estimates',
        exact: true,
        element: <QboQuotesPage />,
      },
      {
        path: 'qbo/create/estimate',
        exact: true,
        element: <CreateEstimatePage />,
      },
      {
        path: 'process-data/forming',
        element: <SummaryPage processType={ProcessNames.FORMING} />,
        exact: true,
      },
      {
        path: 'process-data/forming/:sessionId',
        element: <ProcessDataPage />,
        exact: true,
      },
      {
        path: 'process-data/bonding',
        element: <SummaryPage processType={ProcessNames.BONDING} />,
        exact: true,
      },
      {
        path: 'process-data/bonding/:sessionId',
        element: <ProcessDataPage />,
        exact: true,
      },
      {
        path: '/live/:pressName',
        element: <HMI />,
        exact: true,
      },
      {
        path: '/hmi/:pressId',
        element: <PressHMIPage />,
        exact: true,
      },
      {
        path: '/test-stand',
        element: <TestStandPage />,
        exact: true,
      },
      {
        path: '/travelers/:travelerId',
        element: <TravelerPage />,
        exact: true,
      },
      {
        path: '/shipments',
        element: <ShipmentsPage />,
        exact: true,
      },
      {
        path: '/shipments/create',
        element: <ShipmentsCreatePage />,
        exact: true,
      },
      {
        path: '/organizations',
        element: <OrganizationsPage />,
        exact: true,
      },
      {
        path: '/organization/:orgId',
        element: <OrgDetailPage />,
        exact: true,
      },
      {
        path: '/print/travelers',
        element: <PrintTravelerLabelsPage />,
        exact: true,
      },
      {
        path: '/print/product-label',
        element: <PrintProductLabelPage />,
        exact: true,
      },
    ],
  },
]);

const root = createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider>
      <BackToTopProvider>
        <InitializeFirebaseApp>
          <I18nProvider locale="en" messages={[enMessages]}>
            <RouterProvider router={router} />
          </I18nProvider>
        </InitializeFirebaseApp>
      </BackToTopProvider>
    </ThemeProvider>
  </React.StrictMode>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
